import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { authApi } from '../api/services';
import BrandMark from '../components/BrandMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  AuthShell,
  AUTH_FIELD_CLASS,
  AUTH_FOOTER_LINK_CLASS,
  AUTH_FOOTNOTE_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_PRIMARY_BUTTON,
  AUTH_SUBTITLE_CLASS,
  AUTH_TITLE_CLASS,
} from '@/components/auth/AuthShell';

// ---------------------------------------------------------------------------
// ForgotPasswordPage — in the shared AuthShell, like LoginPage: the dark
// backdrop, no card chrome, .rise staggered entrance and a leave-veil on
// successful submit.
// ---------------------------------------------------------------------------

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Document title parity with Login.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Reset your password · Cadence';
    return () => { document.title = prev; };
  }, []);

  // Entrance — triple-redundant trigger so .rise reveals fire even if React
  // paints late or visibility flips.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const reveal = () => { if (!cancelled) setReady(true); };
    const t1 = window.setTimeout(reveal, 120);
    const t2 = window.setTimeout(reveal, 200);
    window.addEventListener('load', reveal);
    const onVis = () => { if (document.visibilityState === 'visible') reveal(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('load', reveal);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Leave-veil — soft fade up on successful submit so the page doesn't flash.
  const [leaving, setLeaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      // Veil up briefly so the success state lands softly rather than cutting.
      setLeaving(true);
      await new Promise((r) => setTimeout(r, 250));
      setSubmitted(true);
      setLeaving(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setLeaving(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell leaving={leaving} width="form">
      <div
        className={cn('rise mb-5', ready && 'is-revealed')}
        style={{ '--d': '120ms' } as React.CSSProperties}
      >
        <BrandMark size="md" showStratiumLine inlineStratiumTagline accent="light" />
      </div>

      <h1
        className={cn(
          'rise mb-[10px]',
          AUTH_TITLE_CLASS,
          ready && 'is-revealed',
        )}
        style={{ '--d': '260ms' } as React.CSSProperties}
      >
        Reset your password.
      </h1>
      <p
        className={cn(
          'rise mb-[30px]',
          AUTH_SUBTITLE_CLASS,
          ready && 'is-revealed',
        )}
        style={{ '--d': '380ms' } as React.CSSProperties}
      >
        We'll email you a link to set a new one.
      </p>

      {error && (
        <Alert variant="error" className="mb-3 rounded-xl">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {submitted ? (
        <div
          className={cn('rise', ready && 'is-revealed')}
          style={{ '--d': '520ms' } as React.CSSProperties}
        >
          <Alert variant="success" className="mb-6 rounded-xl">
            <AlertDescription>
              If an account exists for <strong>{email}</strong>, we've sent a password reset link. Check your inbox.
            </AlertDescription>
          </Alert>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className={cn('rise flex flex-col gap-[14px]', ready && 'is-revealed')}
          style={{ '--d': '520ms' } as React.CSSProperties}
        >
          <div className="flex flex-col gap-[7px]">
            <Label htmlFor="email" className={AUTH_LABEL_CLASS}>
              Work email
            </Label>
            <Input
              id="email"
              type="email"
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={AUTH_FIELD_CLASS}
            />
          </div>
          <Button
            type="submit"
            disabled={loading || !email}
            className={cn('mt-[6px]', AUTH_PRIMARY_BUTTON)}
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Send reset link'}
          </Button>
        </form>
      )}

      <div
        className={cn(
          'rise mt-6 flex items-center justify-center gap-3 text-xs text-muted-foreground',
          ready && 'is-revealed',
        )}
        style={{ '--d': '1000ms' } as React.CSSProperties}
      >
        <Link to="/login" className={AUTH_FOOTER_LINK_CLASS}>
          Back to sign in
        </Link>
        <span aria-hidden className="text-foreground/30">·</span>
        <Link to="/signup" className={AUTH_FOOTER_LINK_CLASS}>
          14 day free trial
        </Link>
      </div>

      {/* Stratium provenance — quiet anchor line. */}
      <p
        className={cn(
          'rise mt-8 text-center',
          AUTH_FOOTNOTE_CLASS,
          ready && 'is-revealed',
        )}
        style={{ '--d': '1200ms' } as React.CSSProperties}
      >
        Cadence is the product. Stratium is the company behind it.
      </p>
    </AuthShell>
  );
}
